import React, { useMemo, useRef } from "react";
import { RulerInput, ScreenShell, SegmentedToggle } from "../components";
import { displayWeight, parseWeightToLbs, weightMax, weightMin, weightStep } from "../units";
import type { GoalDirection, WeightUnit } from "../types";
import { T } from "../theme";

type Props = {
  step: number;
  total: number;
  /** Effective direction — derived from the current target vs current weight. */
  goal: GoalDirection;
  /** What the user originally picked on the goal screen (or null if not yet). */
  storedGoal: GoalDirection | null;
  currentLbs: number;
  targetLbs: number;
  unit: WeightUnit;
  onUnitChange: (u: WeightUnit) => void;
  onTargetChange: (lbs: number) => void;
  onGoalChange: (g: GoalDirection) => void;
  onNext: () => void;
  onBack: () => void;
  isMobile: boolean;
};

function inferGoalFromDelta(deltaLbs: number): GoalDirection {
  if (Math.abs(deltaLbs) < 0.5) return "maintain";
  return deltaLbs < 0 ? "lose" : "gain";
}

const GOAL_LABELS: Record<GoalDirection, string> = {
  lose: "Lose weight",
  gain: "Build muscle",
  maintain: "Maintain",
};

export function TargetScreen({
  step,
  total,
  goal,
  storedGoal,
  currentLbs,
  targetLbs,
  unit,
  onUnitChange,
  onTargetChange,
  onGoalChange,
  onNext,
  onBack,
  isMobile,
}: Props): React.ReactElement {
  const displayedTarget = displayWeight(targetLbs, unit);
  const displayedCurrent = displayWeight(currentLbs, unit);
  const deltaLbs = targetLbs - currentLbs;
  const deltaDisplay = displayedTarget - displayedCurrent;
  const valid = targetLbs > 0 && targetLbs < 1000;

  // Stable range anchored to current weight, so labels don't reshuffle as the user scrubs.
  const seedByUnit = useRef<{ lb: number | null; kg: number | null }>({ lb: null, kg: null });
  if (seedByUnit.current[unit] === null) {
    seedByUnit.current[unit] = displayedCurrent;
  }
  const seed = seedByUnit.current[unit] ?? displayedCurrent;
  const range = useMemo(() => {
    const span = unit === "lb" ? 80 : 35;
    const minV = Math.max(weightMin(unit), Math.round(seed - span));
    const maxV = Math.min(weightMax(unit), Math.round(seed + span));
    return { min: minV, max: maxV };
  }, [unit, seed]);

  // The chart and math are driven by `goal` (the effective direction inferred
  // from the delta). The mismatch chip compares the *stored* goal — what the
  // user actually picked on the goal screen — to the effective one so we can
  // offer a one-tap switch.
  const inferred = inferGoalFromDelta(deltaLbs);
  const mismatch =
    storedGoal !== null && storedGoal !== "maintain" && inferred !== "maintain" && storedGoal !== inferred;

  const sign = deltaDisplay >= 0 ? "+" : "−";
  const deltaColor = goal === "maintain" ? T.fg2 : mismatch ? T.warn : T.lime;

  const title = goal === "maintain" ? "What weight do you want to hold?" : "What's your target weight?";

  return (
    <ScreenShell
      step={step}
      total={total}
      title={title}
      subtitle="Drag the ruler. We'll do the math."
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel="Continue"
      primaryDisabled={!valid}
      onPrimary={onNext}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
        <SegmentedToggle
          options={[
            { value: "lb", label: "Pounds" },
            { value: "kg", label: "Kilograms" },
          ]}
          value={unit}
          onChange={onUnitChange}
        />
      </div>

      <RulerInput
        value={displayedTarget}
        min={range.min}
        max={range.max}
        step={weightStep(unit)}
        majorStep={unit === "lb" ? 10 : 5}
        unit={unit}
        decimals={unit === "lb" ? 0 : 1}
        onChange={(v) => onTargetChange(parseWeightToLbs(v, unit))}
        accent="lime"
      />

      {/* Delta hero — the actual headline for this screen */}
      <div style={{ marginTop: 26, textAlign: "center" }}>
        <div style={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.muted }}>
          Net change
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: "2.4rem",
            fontWeight: 800,
            letterSpacing: "-0.025em",
            color: deltaColor,
            ...T.tabular,
            lineHeight: 1,
          }}
        >
          {goal === "maintain" || Math.abs(deltaDisplay) < 0.05
            ? `0`
            : `${sign}${Math.abs(deltaDisplay).toFixed(unit === "lb" ? 0 : 1)}`}
          <span style={{ fontSize: "0.9rem", color: T.muted, fontWeight: 700, marginLeft: 6 }}>{unit}</span>
        </div>
        <div style={{ marginTop: 6, color: T.fg2, fontSize: "0.82rem" }}>
          Starting from <span style={{ color: T.fg, fontWeight: 700 }}>{displayedCurrent} {unit}</span>
        </div>
      </div>

      {/* Mismatch warning + one-tap switch */}
      {mismatch ? (
        <div
          style={{
            marginTop: 22,
            padding: "12px 14px",
            borderRadius: 12,
            background: "rgba(245,158,11,0.10)",
            border: "1px solid rgba(245,158,11,0.35)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, color: "#fde68a", fontSize: "0.86rem", lineHeight: 1.45, fontWeight: 600 }}>
            Looks like {inferred === "lose" ? "a cut" : "a gain"} — you picked {storedGoal ? GOAL_LABELS[storedGoal] : "—"}.
          </div>
          <button
            type="button"
            onClick={() => onGoalChange(inferred)}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: T.fg,
              fontSize: "0.82rem",
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Switch to {GOAL_LABELS[inferred]}
          </button>
        </div>
      ) : null}
    </ScreenShell>
  );
}
